/**
 * M07 命中率卡片 · S2 Day 8b
 *
 * 展示 intent_hit_rate / pb_hit_rate · 含图标确保状态不只靠颜色（X3 §12.1）
 */
import { CheckCircle2, MinusCircle, XCircle } from "lucide-react";

import type { RegressionResponse } from "@/lib/api/eval";

interface Props {
	result: RegressionResponse;
}

const THRESHOLD_GOOD = 0.85;
const THRESHOLD_WARN = 0.6;

function tone(rate: number) {
	if (rate >= THRESHOLD_GOOD) {
		return {
			ring: "border-brand-success",
			label: "text-brand-success",
			icon: <CheckCircle2 className="h-4 w-4 text-brand-success" aria-hidden="true" />,
			tag: "达标",
		};
	}
	if (rate >= THRESHOLD_WARN) {
		return {
			ring: "border-brand-warning",
			label: "text-brand-warning",
			icon: <MinusCircle className="h-4 w-4 text-brand-warning" aria-hidden="true" />,
			tag: "可提升",
		};
	}
	return {
		ring: "border-brand-danger",
		label: "text-brand-danger",
		icon: <XCircle className="h-4 w-4 text-brand-danger" aria-hidden="true" />,
		tag: "不达标",
	};
}

function formatPct(n: number): string {
	return `${(n * 100).toFixed(1)}%`;
}

interface CardProps {
	title: string;
	rate: number;
	passed: number;
	total: number;
	testid: string;
	disabled?: boolean;
	disabledHint?: string;
}

function Card({ title, rate, passed, total, testid, disabled, disabledHint }: CardProps) {
	if (disabled) {
		return (
			<div
				className="rounded-md border border-dashed border-surface-border bg-surface-card p-4 text-sm text-text-muted"
				data-testid={testid}
			>
				<div className="font-medium text-text-secondary">{title}</div>
				<div className="mt-2">{disabledHint ?? "未参与本次回归"}</div>
			</div>
		);
	}
	const t = tone(rate);
	return (
		<div
			className={`rounded-md border-l-4 ${t.ring} border-y border-r border-y-surface-border border-r-surface-border bg-surface-card p-4`}
			data-testid={testid}
		>
			<div className="flex items-center justify-between">
				<span className="text-sm font-medium text-text-secondary">{title}</span>
				<span className="inline-flex items-center gap-1 text-xs">
					{t.icon}
					<span className={t.label}>{t.tag}</span>
				</span>
			</div>
			<div className={`mt-2 font-mono text-3xl font-semibold ${t.label}`}>{formatPct(rate)}</div>
			<div className="mt-1 text-xs text-text-muted">
				{passed} / {total}
			</div>
		</div>
	);
}

export function HitRateCards({ result }: Props) {
	const pbDisabled = result.mode === "intent" || result.pb_total_evaluable === 0;
	return (
		<div className="grid grid-cols-1 gap-3 md:grid-cols-2" data-testid="m07-hit-rate-cards">
			<Card
				title="意图命中率"
				rate={result.intent_hit_rate}
				passed={result.intent_passed}
				total={result.total}
				testid="m07-card-intent"
				disabled={result.mode === "playbook"}
				disabledHint="本次仅评方案"
			/>
			<Card
				title="方案命中率"
				rate={result.pb_hit_rate}
				passed={result.pb_passed}
				total={result.pb_total_evaluable}
				testid="m07-card-pb"
				disabled={pbDisabled}
				disabledHint={result.mode === "intent" ? "本次仅评意图" : "无可评方案样本"}
			/>
		</div>
	);
}
